/**
 * The "Daily Life" journey reward: a little earth that gains buildings as stars are earned, so the
 * planet visibly fills with everyday life across the journey. Pure Phaser graphics (no image asset,
 * same approach as game/contours.ts and the ★/☆ progress bar), scaled by the planet radius so it
 * works both small (the menu/summary progress bar) and large (the completion celebration).
 *
 * Usage:
 *   const planet = createDailyLifePlanet(scene, x, y, radius);
 *   planet.setStars(stats.stars, STAR_COUNT);   // reveal structures up to the current star count
 */
import Phaser from 'phaser';

const WATER = 0x2f7fd6;
const LAND = 0x49aa52;
const OUTLINE = 0xffffff;
const ROOF = 0xc0392b;
const WALL = 0xf2e9d8;
const SHOP = 0xe8a13a;
const TOWER = 0x9fb4c7;
const WINDOW = 0x9bd0ff;
const TRUNK = 0x8a5a2b;
const LEAF = 0x2f9e44;

type Kind = 'house' | 'tower' | 'shop' | 'tree';

/**
 * Structures placed left→right around the top of the globe and revealed in order as stars are
 * earned, so the city grows as the journey progresses. `ang` is degrees from the top of the
 * planet (negative = left, positive = right); each structure stands radially out from the surface.
 */
const STRUCTURES: { ang: number; kind: Kind; h: number }[] = [
  { ang: -70, kind: 'tree', h: 0.9 },
  { ang: -56, kind: 'house', h: 1.0 },
  { ang: -42, kind: 'tower', h: 1.4 },
  { ang: -29, kind: 'house', h: 0.9 },
  { ang: -15, kind: 'shop', h: 0.8 },
  { ang: -2, kind: 'tower', h: 1.5 },
  { ang: 11, kind: 'house', h: 1.0 },
  { ang: 24, kind: 'tree', h: 0.85 },
  { ang: 37, kind: 'shop', h: 0.8 },
  { ang: 50, kind: 'tower', h: 1.3 },
  { ang: 63, kind: 'house', h: 0.95 },
  { ang: 76, kind: 'tree', h: 0.9 },
];

export interface PlanetHandle {
  container: Phaser.GameObjects.Container;
  /** Reveal structures proportional to `stars / maxStars` (full city at maxStars). */
  setStars(stars: number, maxStars: number): void;
}

/** Draws one structure as a Graphics whose base sits at local (0,0), growing upward (−y). */
function drawStructure(scene: Phaser.Scene, kind: Kind, u: number, hScale: number): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  const w = u * 2.2;
  if (kind === 'tree') {
    g.fillStyle(TRUNK).fillRect(-u * 0.25, -u * 1.2, u * 0.5, u * 1.2);
    g.fillStyle(LEAF).fillCircle(0, -u * 1.7, u * 1.0);
  } else if (kind === 'tower') {
    const h = u * 3.2 * hScale;
    g.fillStyle(TOWER).fillRect(-w / 2, -h, w, h);
    g.fillStyle(WINDOW);
    for (let row = 0; row < 3; row++) {
      g.fillRect(-w / 2 + u * 0.4, -h + u * 0.5 + row * u * 0.9, u * 0.5, u * 0.5);
      g.fillRect(u * 0.1, -h + u * 0.5 + row * u * 0.9, u * 0.5, u * 0.5);
    }
  } else if (kind === 'shop') {
    const h = u * 1.6 * hScale;
    g.fillStyle(SHOP).fillRect(-w / 2, -h, w, h);
    g.fillStyle(WALL).fillRect(-u * 0.4, -h * 0.55, u * 0.8, h * 0.55); // door
  } else {
    // house: walls + triangular roof + a window
    const h = u * 1.8 * hScale;
    g.fillStyle(WALL).fillRect(-w / 2, -h, w, h);
    g.fillStyle(ROOF).fillTriangle(-w / 2 - u * 0.2, -h, w / 2 + u * 0.2, -h, 0, -h - u * 1.1);
    g.fillStyle(WINDOW).fillRect(-u * 0.35, -h * 0.7, u * 0.7, u * 0.7);
  }
  return g;
}

export function createDailyLifePlanet(
  scene: Phaser.Scene, x: number, y: number, radius: number,
): PlanetHandle {
  const container = scene.add.container(x, y);

  // The globe: water disc with a couple of land masses and a white rim.
  const globe = scene.add.graphics();
  globe.fillStyle(WATER).fillCircle(0, 0, radius);
  globe.fillStyle(LAND);
  globe.fillEllipse(-radius * 0.35, radius * 0.15, radius * 0.9, radius * 0.6);
  globe.fillEllipse(radius * 0.4, -radius * 0.3, radius * 0.7, radius * 0.5);
  globe.fillEllipse(radius * 0.15, radius * 0.55, radius * 0.6, radius * 0.35);
  globe.lineStyle(Math.max(2, radius * 0.06), OUTLINE, 1).strokeCircle(0, 0, radius);
  container.add(globe);

  // Structures: pre-create all, hidden; setStars() reveals them left→right.
  const u = radius * 0.16;
  const structures: Phaser.GameObjects.Graphics[] = [];
  for (const s of STRUCTURES) {
    const g = drawStructure(scene, s.kind, u, s.h);
    const rad = Phaser.Math.DegToRad(s.ang);
    // Base point on the surface (top of circle is straight up), structure pointing radially out.
    g.setPosition(Math.sin(rad) * radius * 0.99, -Math.cos(rad) * radius * 0.99);
    g.setRotation(rad);
    g.setVisible(false);
    structures.push(g);
    container.add(g);
  }

  const setStars = (stars: number, maxStars: number): void => {
    const frac = maxStars > 0 ? Phaser.Math.Clamp(stars / maxStars, 0, 1) : 0;
    const reveal = Math.round(frac * structures.length);
    structures.forEach((g, i) => g.setVisible(i < reveal));
  };

  return { container, setStars };
}
